<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <app-card-actions
    v-if="aggregationList.length > 0"
    class="daily-report-contents-wrapper mb-6"
    action-collapse
  >
    <template #title>
      リアルタイム集計
    </template>

    <v-card-text v-if="loading || error">
      <v-skeleton-loader
        v-if="loading"
        type="table"
      />
      <v-alert
        v-else-if="error"
        type="error"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-card-text v-else>
      <!-- 下位タブ: 集計表別 -->
      <v-tabs
        v-model="aggregationTab"
        show-arrows
        grow
        height="58"
      >
        <v-tab
          v-for="agg in aggregationList"
          :key="agg.id"
        >
          {{ agg.name }}
        </v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="aggregationTab"
        touchless
        style="background: transparent"
      >
        <v-tab-item
          v-for="agg in aggregationList"
          :key="agg.id"
        >
          <div class="d-flex align-center mt-3 mb-2">
            <v-btn
              outlined
              color="primary"
              :loading="resultLoading[agg.id]"
              class="me-3"
              style="min-width: 120px; height: 40px;"
              @click="fetchAggregationResult(agg.id)"
            >
              <v-icon class="me-1">{{ icons.mdiRefresh }}</v-icon>
              更新
            </v-btn>
            <span v-if="lastFetchedAt[agg.id]" class="text--secondary text-body-2">
              最終取得: {{ lastFetchedAt[agg.id] }}
            </span>
            <v-spacer />
            <v-dialog
              v-model="userDialogOpen[agg.id]"
              max-width="400"
              scrollable
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  small
                  text
                  v-bind="attrs"
                  v-on="on"
                  @click="fetchAvailableUsers(agg.id)"
                >
                  <v-icon small class="me-1">{{ icons.mdiAccountCog }}</v-icon>
                  適用ユーザー管理
                </v-btn>
              </template>
              <v-card style="max-height: 70vh; overflow-y: auto;">
                <v-card-title>適用ユーザー管理</v-card-title>
                <v-card-text>
                  <v-list v-if="agg.aggregation_users && agg.aggregation_users.length > 0">
                    <v-list-item
                      v-for="au in agg.aggregation_users"
                      :key="au.id"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ au.user_name }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          icon
                          small
                          :loading="userActionLoading"
                          @click="removeUser(agg.id, au.id)"
                        >
                          <v-icon small>{{ icons.mdiClose }}</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <p v-else class="text--secondary">
                    適用ユーザーがいません
                  </p>

                  <v-divider class="my-4" />

                  <v-combobox
                    v-model="selectedUser"
                    :items="availableUsers"
                    item-text="name"
                    item-value="id"
                    label="新しくユーザーを紐付ける"
                    placeholder="ユーザーを選択してください"
                    no-data-text="利用可能なユーザーがありません"
                    return-object
                    :loading="availableUsersLoading"
                    @change="onUserSelect(agg.id, $event)"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="userDialogOpen[agg.id] = false">
                    閉じる
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>

          <v-skeleton-loader
            v-if="resultLoading[agg.id]"
            type="table"
          />
          <v-alert
            v-else-if="resultErrors[agg.id]"
            type="error"
          >
            {{ resultErrors[agg.id] }}
          </v-alert>
          <read-only-aggregation-table
            v-else-if="aggregationResults[agg.id]"
            :aggregation="toAggregationProp(agg.id)"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </app-card-actions>
</template>

<script>
import Vue from 'vue'
import {
  ref,
  reactive,
  watch,
  onMounted,
} from '@vue/composition-api'
import { mdiClose, mdiAccountCog, mdiRefresh } from '@mdi/js'
import AppCardActions from '@core/components/app-card-actions/AppCardActions.vue'
import ReadOnlyAggregationTable from '@/views/v2-temp/eod-closing/components/ReadOnlyAggregationTable.vue'
import RealtimeAggregationApi from '@/api/v2/eod-report/RealtimeAggregation'
import RealtimeAggregationUserApi from '@/api/v2/eod-report/RealtimeAggregationUser'

export default {
  components: {
    AppCardActions,
    ReadOnlyAggregationTable,
  },
  props: {
    date: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const loading = ref(false)
    const error = ref(null)
    const aggregationList = ref([])
    const aggregationTab = ref(0)
    const aggregationResults = reactive({})
    const resultLoading = reactive({})
    const resultErrors = reactive({})
    const lastFetchedAt = reactive({})

    // ユーザ管理用
    const userDialogOpen = reactive({})
    const availableUsers = ref([])
    const availableUsersLoading = ref(false)
    const userActionLoading = ref(false)
    const selectedUser = ref(null)

    // APIレスポンスを ReadOnlyAggregationTable が期待する aggregation prop の形式に変換
    const toAggregationProp = aggId => {
      const result = aggregationResults[aggId]
      const aggInfo = aggregationList.value.find(a => a.id === aggId)
      if (!result || !aggInfo) return { attributes: {} }

      // result.data は [{ user_id, user_name, results: [{ rule_id, rule_name, value }] }]
      const users = result.data || []

      // ルール情報はレスポンスの最初のユーザーの結果から取得
      const businessRules = users.length > 0
        ? users[0].results.map(r => ({
          id: r.rule_id,
          name: r.rule_name,
          prefix: '',
          suffix: '',
        }))
        : []

      return {
        attributes: {
          id: aggId,
          name: aggInfo.name,
          description: null,
          type: 'RealtimeUserAggregation',
          businessRules: {
            data: businessRules.map(br => ({
              id: String(br.id),
              type: 'business_rule',
              attributes: {
                id: br.id,
                name: br.name,
                description: null,
              },
            })),
          },
          contexts: {
            data: users.map(user => ({
              attributes: {
                contextType: 'User',
                id: user.user_id,
                name: user.user_name,
                results: {
                  data: user.results.map(r => ({
                    attributes: {
                      businessRuleId: r.rule_id,
                      businessRuleResultId: r.value != null && r.value !== '' ? -1 : null,
                      value: r.value != null ? String(r.value) : null,
                      dependentResources: null,
                      dependentBusinessRuleIds: [],
                      editable: false,
                    },
                  })),
                },
              },
            })),
          },
        },
      }
    }

    const fetchAggregationList = async () => {
      loading.value = true
      error.value = null
      try {
        const res = await RealtimeAggregationApi.getAggregations()
        aggregationList.value = res.data.data || []
      } catch (e) {
        error.value = 'リアルタイム集計の取得に失敗しました'
      } finally {
        loading.value = false
      }
    }

    const fetchAggregationResult = async aggId => {
      if (!props.date) return
      Vue.set(resultLoading, aggId, true)
      Vue.set(resultErrors, aggId, null)
      try {
        const res = await RealtimeAggregationApi.getAggregation(aggId, props.date)
        Vue.set(aggregationResults, aggId, res.data)
        Vue.set(lastFetchedAt, aggId, new Date().toLocaleTimeString('ja-JP'))
      } catch (e) {
        Vue.set(resultErrors, aggId, 'データの取得に失敗しました')
      } finally {
        Vue.set(resultLoading, aggId, false)
      }
    }

    const fetchAllResults = async () => {
      if (!props.date || aggregationList.value.length === 0) return
      await Promise.all(
        aggregationList.value.map(agg => fetchAggregationResult(agg.id)),
      )
    }

    const fetchAvailableUsers = async aggId => {
      if (availableUsersLoading.value) return
      availableUsersLoading.value = true
      try {
        const res = await RealtimeAggregationUserApi.getAvailableUsers(aggId)
        availableUsers.value = res.data.data || []
      } catch (e) {
        availableUsers.value = []
      } finally {
        availableUsersLoading.value = false
      }
    }

    const onUserSelect = async (aggId, item) => {
      if (!item?.id || userActionLoading.value) return
      userActionLoading.value = true
      try {
        const res = await RealtimeAggregationUserApi.createAggregationUser(aggId, item.id)
        if (res.data.status === 'success') {
          const newAu = res.data.data
          const agg = aggregationList.value.find(a => a.id === aggId)
          if (agg) {
            agg.aggregation_users.push({ id: newAu.id, user_id: newAu.user_id, user_name: item.name })
          }
          availableUsers.value = availableUsers.value.filter(u => u.id !== item.id)
          fetchAggregationResult(aggId)
        }
      } finally {
        userActionLoading.value = false
        selectedUser.value = null
      }
    }

    const removeUser = async (aggId, aggregationUserId) => {
      if (userActionLoading.value) return
      userActionLoading.value = true
      try {
        const res = await RealtimeAggregationUserApi.destroyAggregationUser(aggId, aggregationUserId)
        if (res.data.status === 'success') {
          const agg = aggregationList.value.find(a => a.id === aggId)
          if (agg) {
            const removed = agg.aggregation_users.find(au => au.id === aggregationUserId)
            agg.aggregation_users = agg.aggregation_users.filter(au => au.id !== aggregationUserId)
            if (removed) {
              availableUsers.value.push({ id: removed.user_id, name: removed.user_name })
            }
          }
          fetchAggregationResult(aggId)
        }
      } finally {
        userActionLoading.value = false
      }
    }

    watch(() => props.date, () => {
      fetchAllResults()
    })

    onMounted(async () => {
      await fetchAggregationList()
      await fetchAllResults()
    })

    return {
      loading,
      error,
      aggregationList,
      aggregationTab,
      aggregationResults,
      resultLoading,
      resultErrors,
      lastFetchedAt,
      toAggregationProp,
      fetchAggregationResult,

      // ユーザ管理
      userDialogOpen,
      availableUsers,
      availableUsersLoading,
      userActionLoading,
      selectedUser,
      fetchAvailableUsers,
      onUserSelect,
      removeUser,

      icons: {
        mdiClose,
        mdiAccountCog,
        mdiRefresh,
      },
    }
  },
}
</script>
